import getUID from '@jetbrains/ring-ui/components/global/get-uid'
import memoize from '@jetbrains/ring-ui/components/global/memoize'
import Loader from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import Shortcuts from '@jetbrains/ring-ui/components/shortcuts/shortcuts'
import classNames from 'classnames'
import * as React from 'react'
import {Suspense} from 'react'
import AutoSizer from 'react-virtualized/dist/es/AutoSizer'
import type {ListProps} from 'react-virtualized/dist/es/List'
import List from 'react-virtualized/dist/es/List'

import type {AppDispatch} from '../../../actions/types'
import {PlaceIdList} from '../../../plugins/plugins.types'
import {getStatusKey} from '../../../rest/schemata'
import type {StatusKey} from '../../../rest/schemata'
import {actions, matchSidebarItem} from '../../../selectors/projectTrees'
import {ALL_PROJECTS_TITLE_ID, ROOT_PROJECT_ID} from '../../../types'
import type {
  BuildTypeId,
  ProjectId,
  ProjectOrBuildTypeNode,
  ProjectOrBuildTypeStatus,
} from '../../../types'
import {
  cloneActiveItem,
  getNodeFromItem,
  getProjectTreeItemId,
  getProjectTreeItemStatusRequest,
} from '../../../types/projectTrees'
import type {ProjectsTreeItem, SidebarActiveItem} from '../../../types/projectTrees'
import {noop} from '../../../utils/empty'
import type {KeyValue} from '../../../utils/object'
import Plugin from '../Plugin/Plugin'
import type {CheckedItem} from '../ProjectBuildTypeSelect/ProjectBuildTypeSelect.types'
import {TreeEventType} from '../Sidebar/Sidebar.types'
import {keyboardToEditTreeEvent, keyboardToTreeEvent} from '../Sidebar/Sidebar.utils'
import SidebarSearch from '../SidebarSearch/SidebarSearch.container'
import type {SidebarSearchOwnProps} from '../SidebarSearch/SidebarSearch.types'

import ProjectsTreePlaceholder from './ProjectsTree.placeholder'
import type {MoveParams} from './ProjectsTreeItem/ProjectsTreeItem'
import {
  FIRST_TITLE_ITEM_HEIGHT,
  NOT_AUTHORIZED_SERVER_ITEM_HEIGHT,
  REGULAR_ITEM_HEIGHT,
  SERVER_ITEM_HEIGHT,
  StyledProjectsTreeItem,
  TITLE_ITEM_HEIGHT,
} from './ProjectsTreeItem/ProjectsTreeItem'
import itemStyles from './ProjectsTreeItem/ProjectsTreeItem.css'

import styles from './ProjectsTree.css'

const containerProps = {
  'data-sidebar-list': true,
}
const ProjectsTreeList = React.forwardRef(function ProjectsTreeList(
  props: ListProps,
  ref: React.Ref<List>,
) {
  return (
    <AutoSizer>
      {React.useCallback(
        ({width, height}) => (
          <List {...props} ref={ref} width={width} height={height} />
        ),
        [props, ref],
      )}
    </AutoSizer>
  )
})
type DefaultProps = {
  onSelect: (item: ProjectsTreeItem) => unknown
  onCheck: (item: ProjectsTreeItem, checked: boolean) => unknown
  toggleExpandAll: (arg0: boolean) => unknown
  reloadProjects: () => unknown
  searchQuery: string
  collapsed: boolean
  ready: boolean
  isEdit: boolean
  isPopup: boolean
  userCanEdit: boolean
  renderSearch: (props: SidebarSearchOwnProps) => React.ReactNode
  projectsSelectable: boolean
}
type State = {
  selected: number
  prevSearchQuery?: string | null | undefined
  prevSearchActive?: boolean | null | undefined
  prevActiveIndex?: number
  prevActiveItem?: SidebarActiveItem | null | undefined
  prevSelectedIndex?: number
  prevSelectedItem?: ProjectsTreeItem | null | undefined
  shouldFocusSelected: boolean
  shouldScrollToSelected: boolean
  showPopupFor: string | null
}
type Props = DefaultProps & {
  items: ReadonlyArray<ProjectsTreeItem>
  statuses?: KeyValue<StatusKey, ProjectOrBuildTypeStatus> | null | undefined
  className?: string
  contentClassName?: string
  itemParentClassName?: string
  activeItem?: SidebarActiveItem | null | undefined
  checkedItems?: ReadonlyArray<CheckedItem>
  changeActiveItem?: ((item: ProjectsTreeItem) => unknown) | null | undefined
  toggleExpandState: (
    projectId: ProjectId,
    expanded: boolean,
    group: 'favorites' | 'all' | 'search' | 'none',
  ) => unknown
  toggleOverview?: (node: ProjectOrBuildTypeNode, on: boolean, skipSubprojects?: boolean) => unknown
  moveProject?: (arg0: ProjectId, arg1: number) => unknown
  moveBuildType?: (arg0: BuildTypeId, arg1: number) => unknown
  searchActive?: boolean | null | undefined
  width?: number | null | undefined
  dispatch: AppDispatch
  children?: React.ReactNode
  placeholder?: React.ReactNode
}
type ShortcutsMap = {
  left: (e: Event) => void
}

const findActiveIndex = (
  item: SidebarActiveItem | null | undefined,
  items: ReadonlyArray<ProjectsTreeItem>,
) => {
  let activeIndex = items.findIndex(matchSidebarItem(item))

  // If there's no item in `favorites`, we should look for it group 'all'.
  // This is because we assume that by default we assume that the item might be in `favorites`,
  // but it's not always the case.
  if (activeIndex === -1 && item != null && item.group === 'favorites') {
    const originalItem = cloneActiveItem(item)

    if (originalItem.nodeType === 'bt' || originalItem.nodeType === 'project') {
      originalItem.group = 'all'
      activeIndex = items.findIndex(matchSidebarItem(originalItem))
    }
  }

  return activeIndex
}

const isItemNonActionable = (item: ProjectsTreeItem): boolean =>
  (item.itemType === 'title' && item.hasChildren !== true) || item.itemType === 'server'

function calculateRowHeight(
  index: number,
  items: ReadonlyArray<ProjectsTreeItem | null | undefined>,
) {
  const item: ProjectsTreeItem | null | undefined = items[index]

  // item == null for loader
  if (item == null || item.itemType === 'title') {
    return index === 0 ? FIRST_TITLE_ITEM_HEIGHT : TITLE_ITEM_HEIGHT
  } else if (item.itemType === 'server') {
    return !item.authorized || item.loading ? NOT_AUTHORIZED_SERVER_ITEM_HEIGHT : SERVER_ITEM_HEIGHT
  } else {
    return REGULAR_ITEM_HEIGHT
  }
}

export const calculateRowOffset = (
  index: number,
  items: ReadonlyArray<ProjectsTreeItem | null | undefined>,
): number => items.slice(0, index).reduce((acc, _, i) => acc + calculateRowHeight(i, items), 0)

type IdProps = {
  buildTypeId: BuildTypeId | null | undefined
  projectId: ProjectId | null | undefined
}
export const getIdProps = (item: ProjectsTreeItem): IdProps => ({
  buildTypeId: item.itemType === 'buildType' || item.itemType === 'template' ? item.id : null,
  projectId: item.itemType === 'project' ? item.id : null,
})

class ProjectsTree extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    collapsed: false,
    isEdit: false,
    isPopup: false,
    userCanEdit: false,
    projectsSelectable: true,
    ready: false,
    searchQuery: '',
    reloadProjects: noop,
    onSelect: noop,
    onCheck: noop,
    renderSearch: (props: SidebarSearchOwnProps) => <SidebarSearch {...props} />,
    toggleExpandAll: noop,
  }

  static getDerivedStateFromProps(
    {searchActive, searchQuery, items, activeItem, isPopup, projectsSelectable}: Props,
    {
      prevSearchActive,
      prevSearchQuery,
      prevActiveItem,
      prevSelectedIndex,
      prevSelectedItem,
      selected,
      shouldScrollToSelected,
    }: State,
  ): Partial<State> {
    const activeIndex = findActiveIndex(activeItem, items)
    let selectedIndex = selected === -1 ? activeIndex : selected
    let selectedItem = items[selectedIndex]
    const commonState = {
      prevSearchActive: searchActive,
      prevSearchQuery: searchQuery,
      prevActiveIndex: activeIndex,
      prevActiveItem: activeItem,
    }
    const activeItemChanged = activeIndex !== -1 && activeItem !== prevActiveItem

    if (
      searchActive === true &&
      (prevSearchActive !== true || searchQuery !== prevSearchQuery) &&
      !(isPopup && activeItemChanged)
    ) {
      // select first match after opening search or changing query
      selectedIndex = items.findIndex(
        item => item.isFullMatch === true && (projectsSelectable || item.itemType !== 'project'),
      )

      /*
       * Case: It's the first open tree without any searching coincidences
       * Result: Selected should be the first item
       */
      if (isPopup && selectedIndex === -1 && activeIndex === -1) {
        selectedIndex = items.findIndex(item => projectsSelectable || item.itemType !== 'project')
      }
    } else if (activeItemChanged) {
      // select active item on navigation
      selectedIndex = activeIndex
    } else if (
      selectedIndex === prevSelectedIndex &&
      prevSelectedItem &&
      selectedItem &&
      selectedItem.id !== prevSelectedItem.id
    ) {
      // Preserve selection when the beginning of the list has changed
      const newSelectedIndex = items.findIndex(item => {
        if (item.id === prevSelectedItem.id && item.itemType === prevSelectedItem.itemType) {
          if (item.itemType === 'project' || item.itemType === 'buildType') {
            return item.group === prevSelectedItem.group
          }

          if (item.itemType === 'title') {
            return item.name === prevSelectedItem.name
          }

          return true
        }

        return false
      })

      if (newSelectedIndex >= 0) {
        selectedIndex = newSelectedIndex
      }
    }

    selectedItem = items[selectedIndex]

    if (selectedItem != null && prevSelectedIndex != null && isItemNonActionable(selectedItem)) {
      selectedIndex += selectedIndex > prevSelectedIndex ? 1 : -1

      if (selectedIndex < 0 || selectedIndex > items.length - 1) {
        selectedIndex = prevSelectedIndex
      }
    }

    return {
      ...commonState,
      selected: selectedIndex,
      prevSelectedIndex: selectedIndex,
      prevSelectedItem: items[selectedIndex],
      shouldScrollToSelected: selectedIndex !== selected ? true : shouldScrollToSelected,
    }
  }

  scrollBarWidth: number | null | undefined = null
  list: List | null | undefined = null
  tree: HTMLElement | null | undefined = null
  search: HTMLInputElement | null | undefined = null
  sidebarRef: {
    current: null | HTMLDivElement
  } = React.createRef<any>()

  shortcutsScope: string = getUID('projects-tree-')
  shortcutsMap: ShortcutsMap = {
    left: e => {
      if (
        e.target instanceof HTMLElement &&
        (e.target.dataset.mainRouter || e.target.dataset.mainPanel)
      ) {
        this.setState({
          shouldFocusSelected: true,
          shouldScrollToSelected: true,
        })
      }
    },
  }

  pageStartIndex = -1
  pageEndIndex = -1
  blurTO: number | null | undefined
  getSearchPattern: (query: string) => string | null | undefined = memoize((query: string) => {
    if (!query) {
      return null
    }

    const rt = query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&').trim()

    if (!rt) {
      return null
    }

    return `(${rt.replace(/ +/g, ' ').replace(/\s/g, ')|(')})`
  })

  state: State = {
    selected: -1,
    shouldFocusSelected: false,
    shouldScrollToSelected: false,
    showPopupFor: null,
  }

  // noinspection JSCheckFunctionSignatures
  componentDidUpdate(prevProps: Props, prevState: State) {
    const {statuses, activeItem, items, searchQuery, searchActive} = this.props
    const {selected} = this.state
    const prevSelectedItem = prevProps.items[prevState.selected]
    const selectedItem = items[selected]
    const list = this.list

    if (list != null) {
      if (
        selected !== prevState.selected &&
        selectedItem != null &&
        prevSelectedItem != null &&
        selectedItem.itemType === prevSelectedItem.itemType &&
        selectedItem.id === prevSelectedItem.id
      ) {
        const prevSelectedOffset = calculateRowOffset(prevState.selected, prevProps.items)
        const selectedOffset = calculateRowOffset(selected, items)
        const currentScrollOffset = list.getOffsetForRow({})
        const offset = currentScrollOffset + selectedOffset - prevSelectedOffset

        if (offset > TITLE_ITEM_HEIGHT) {
          list.scrollToPosition(offset)
        }
      }

      if (
        prevProps.statuses !== statuses ||
        prevProps.activeItem?.nodeType !== activeItem?.nodeType ||
        prevProps.activeItem?.id !== activeItem?.id ||
        prevProps.searchQuery !== searchQuery ||
        prevProps.items !== items
      ) {
        list.forceUpdateGrid()
      }

      if (
        (searchActive !== true && items.length !== prevProps.items.length) ||
        (searchActive === true && !prevProps.searchActive) ||
        items !== prevProps.items
      ) {
        list.recomputeRowHeights()
      }
    }
  }

  focusList: () => void = () => {
    this.tree?.querySelector<HTMLElement>('[data-sidebar-list]')?.focus()
  }

  setListRef: (arg0: List | null | undefined) => void = el => {
    this.list = el
  }

  setTreeRef: (arg0: HTMLElement | null | undefined) => void = el => {
    this.tree = el
  }

  setSearchRef: (arg0: HTMLInputElement | null | undefined) => void = el => {
    this.search = el
  }

  rowRenderer: (arg0: {
    index: number
    isScrolling: boolean
    style: KeyValue<string, string | number>
  }) => React.ReactNode = ({
    index,
    style,
    isScrolling,
  }: {
    index: number
    isScrolling: boolean
    style: KeyValue<string, string | number>
  }): React.ReactNode => {
    const {
      items,
      statuses,
      searchQuery,
      toggleOverview,
      reloadProjects,
      userCanEdit,
      checkedItems = [],
    } = this.props
    const {selected, prevActiveIndex = -1, shouldFocusSelected}: State = this.state
    const item: ProjectsTreeItem | null | undefined = items[index]

    if (item == null) {
      return (
        <div role="row" className={styles.loader} style={style} key={`loader_${index}`}>
          <Loader />
          <span role="cell" className={styles.loaderText}>
            {' '}
            {'Projects are loading...'}
          </span>
        </div>
      )
    }

    const statusRequest = getProjectTreeItemStatusRequest(item)
    const statusKey = statusRequest ? getStatusKey(statusRequest) : null
    const statusInfo: ProjectOrBuildTypeStatus | null | undefined =
      statuses && statusKey ? statuses[statusKey] : null
    const id = getProjectTreeItemId(item)
    const handlerSetShowPopup = (show: boolean) => {
      if (!isScrolling) {
        this.setState({showPopupFor: show ? id : null})
      }
    }
    const checkedItem = !isScrolling
      ? checkedItems.find(({node}) => matchSidebarItem(node)(item))
      : null
    const isActive = item === items[prevActiveIndex]

    return (
      <StyledProjectsTreeItem
        isFavorites={item.group === 'favorites'}
        id={id}
        key={id}
        {...getIdProps(item)}
        authorized={item.itemType === 'server' ? item.authorized : null}
        loading={item.itemType === 'server' ? item.loading : null}
        serverId={item.serverId}
        style={style}
        isScrolling={isScrolling}
        itemType={item.itemType}
        href={item.itemType === 'link' ? item.href : null}
        icon={item.icon}
        name={item.name}
        collapsible={item.hasChildren}
        expanded={item.expanded}
        focusable={item.focusable}
        level={item.level}
        type={item.type}
        archived={item.archived}
        paused={item.paused}
        selected={selected === index}
        shouldFocus={!isScrolling && selected === index && shouldFocusSelected}
        active={isActive}
        onClick={this.onItemClick}
        onChevronClick={this.onItemChevronClick}
        onFocus={this.onItemFocus}
        index={index}
        searchPattern={this.getSearchPattern(searchQuery)}
        status={statusInfo?.status}
        investigation={statusInfo?.investigation}
        systemProblems={statusInfo?.systemProblems}
        hasOwnPending={statusInfo?.hasOwnPending}
        pendingCount={statusInfo?.pending ?? 0}
        runningCount={statusInfo?.running ?? 0}
        queuedCount={statusInfo?.queued ?? 0}
        showStarAction={userCanEdit && item.group === 'search' && toggleOverview != null}
        isStarred={item.isStarred === true}
        editable={userCanEdit && item.editable}
        newFailedTestsCountsForRunningBuilds={statusInfo?.failedCounts?.inRunning}
        newFailedTestsCountForLatestBuild={statusInfo?.failedCounts?.inLatestFinished ?? 0}
        toggleOverview={toggleOverview ?? noop}
        reloadProjects={reloadProjects}
        getWidth={this.getItemWidth}
        isFirstSibling={
          item ===
          items.find(
            otherItem =>
              otherItem.group === item.group &&
              otherItem.itemType === item.itemType &&
              otherItem.parentId === item.parentId,
          )
        }
        onMove={this.handleMove}
        checked={checkedItem?.state === 'CHECKED'}
        indeterminate={checkedItem?.state === 'INDETERMINATE'}
        onCheck={this.onItemCheck}
        isDisabled={item.isDisabled === true}
        hasReorderedChildren={item.hasReorderedChildren === true}
        showPathInTitle={
          item.parentId === ROOT_PROJECT_ID && item.actualParentId !== ROOT_PROJECT_ID
        }
        showPopup={this.state.showPopupFor === id}
        setShowPopup={handlerSetShowPopup}
      />
    )
  }

  onKeyDown: (event: React.KeyboardEvent<EventTarget>) => void = event => {
    const {
      toggleExpandState,
      toggleExpandAll,
      items,
      isEdit,
      changeActiveItem,
      onSelect,
      dispatch,
      toggleOverview,
    } = this.props
    const {selected} = this.state
    const itemsCount = items.length

    if (itemsCount === 0) {
      return
    }

    // Make sure that the focus is on the sidebar, which is not the case,
    // if the event is received from a link in the popup, which is physically outside.
    if (event.target instanceof Node && !this.sidebarRef.current?.contains(event.target)) {
      this.focusList()
    }

    // Checkbox toggle
    if (event.target instanceof HTMLInputElement && event.key === ' ') {
      return
    }

    // Typing a character, shift is OK
    if (
      event.key === 'Backspace' ||
      (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey)
    ) {
      this.focusQueryIfSearchIsActive()
      return
    }

    if (
      event.key === 'Enter' &&
      !isEdit &&
      onSelect === noop &&
      event.target instanceof Node &&
      this.tree?.contains(event.target)
    ) {
      return
    }

    const treeEvent =
      (isEdit && keyboardToEditTreeEvent(event, items, selected)) ||
      keyboardToTreeEvent(
        event,
        items,
        selected > -1 ? selected : this.pageStartIndex,
        this.pageStartIndex,
        this.pageEndIndex,
      )

    if (treeEvent == null) {
      return
    }

    if (treeEvent.type === TreeEventType.EVENT_TYPE_FOCUS) {
      if (treeEvent.index < 0) {
        this.focusQueryIfSearchIsActive()
      } else if (treeEvent.index < items.length) {
        this.select(() => treeEvent.index)
      }
    }

    if (treeEvent.type === TreeEventType.EVENT_TYPE_MOVE_UP) {
      this.moveSelectedItem(-1)
    }

    if (treeEvent.type === TreeEventType.EVENT_TYPE_MOVE_DOWN) {
      this.moveSelectedItem(1)
    }

    const item = items[treeEvent.index]

    if (treeEvent.type === TreeEventType.EVENT_TYPE_EXPAND) {
      if (item.id === ALL_PROJECTS_TITLE_ID) {
        toggleExpandAll(true)
      } else if (item.itemType === 'project') {
        toggleExpandState(item.id, true, item.group)
      }
    }

    if (treeEvent.type === TreeEventType.EVENT_TYPE_COLLAPSE) {
      if (item.id === ALL_PROJECTS_TITLE_ID) {
        toggleExpandAll(false)
      } else if (item.itemType === 'project') {
        toggleExpandState(item.id, false, item.group)
      }
    }

    if (treeEvent.type === TreeEventType.EVENT_TYPE_SELECT) {
      if (item.itemType !== 'title' && changeActiveItem != null) {
        event.preventDefault()
        changeActiveItem(item)
      }

      if (isEdit) {
        const node = getNodeFromItem(item)

        if (node != null && toggleOverview != null) {
          toggleOverview(node, !item.isStarred, event.ctrlKey || event.metaKey)
        }
      }

      if (item.itemType === 'action') {
        dispatch(typeof item.action === 'string' ? actions[item.action]() : item.action())
      }

      onSelect(item)
    }
  }

  onItemClick: (arg0: number) => void = index => {
    this.select(() => index)
    const {items, dispatch, onSelect} = this.props
    const item = items[index]
    onSelect(item)

    if (item.itemType === 'action') {
      dispatch(typeof item.action === 'string' ? actions[item.action]() : item.action())
    }
  }

  onItemCheck: (arg0: number, arg1: boolean) => void = (index, checked) => {
    const {items, onCheck} = this.props
    const item = items[index]
    onCheck(item, checked)
  }

  onItemChevronClick: (arg0: number) => void = index => {
    const {items, toggleExpandState, toggleExpandAll}: Props = this.props
    this.select(() => index)
    const item: ProjectsTreeItem = items[index]

    switch (item.itemType) {
      case 'title':
        toggleExpandAll(!item.expanded)
        break

      case 'project':
        toggleExpandState(item.id, !item.expanded, item.group)
        break

      default:
    }
  }

  onItemFocus: (arg0: number) => void = index => {
    this.select(() => index)
  }

  onTreeBlur: (arg0: React.FocusEvent) => void = e => {
    if (e.relatedTarget instanceof Node && !this.tree?.contains(e.relatedTarget)) {
      this.blurTO = window.setTimeout(
        () =>
          this.setState({
            shouldFocusSelected: false,
            shouldScrollToSelected: false,
          }),
        0,
      )
    }
  }

  onTreeFocus: () => void = () => {
    if (this.blurTO != null) {
      window.clearTimeout(this.blurTO)
      this.blurTO = null
    }
  }

  onListScroll: () => void = () => {
    setTimeout(
      () =>
        this.setState({
          shouldScrollToSelected: false,
        }),
      0,
    )
  }

  calculateRowHeight: (arg0: {index: number}) => number = ({index}) =>
    calculateRowHeight(index, this.props.items)

  onScrollbarPresenceChange: (arg0: {size: number; vertical: boolean}) => void = ({
    size,
    vertical,
  }) => {
    this.scrollBarWidth = vertical ? size : 0
  }

  getItemWidth: () => null | number = () => {
    const {width} = this.props
    return width != null && width > 0 ? width - (this.scrollBarWidth || 0) : null
  }

  handleMove: (arg0: MoveParams) => void = ({projectId, buildTypeId, shiftBy}) => {
    const {moveProject, moveBuildType} = this.props

    if (buildTypeId != null && moveBuildType != null) {
      moveBuildType(buildTypeId, shiftBy)
    } else if (projectId != null && moveProject != null) {
      moveProject(projectId, shiftBy)
    }
  }

  onRowsRendered: (arg0: {startIndex: number; stopIndex: number}) => void = ({
    startIndex,
    stopIndex,
  }) => {
    this.pageStartIndex = startIndex
    this.pageEndIndex = stopIndex
  }

  moveSelectedItem(shiftBy: number) {
    const {selected} = this.state

    if (selected === -1) {
      return
    }

    const {items} = this.props
    const selectedItem = items[selected]

    if (selectedItem.group === 'favorites') {
      this.handleMove({...getIdProps(selectedItem), shiftBy})
    }
  }

  select(getIndex: (arg0: number) => number) {
    this.setState(({selected}) => ({
      selected: getIndex(selected),
      shouldFocusSelected: true,
      shouldScrollToSelected: true,
    }))
  }

  focusQueryIfSearchIsActive() {
    if (this.search != null && this.props.searchActive === true) {
      this.search.focus()
    }
  }

  render(): React.ReactNode {
    const {
      items,
      className,
      contentClassName,
      itemParentClassName,
      collapsed,
      activeItem,
      checkedItems,
      ready,
      children,
      placeholder,
      searchActive,
      searchQuery,
    }: Props = this.props
    const {selected, shouldScrollToSelected}: State = this.state
    const rowCount = ready ? items.length : items.length + 1
    const classes = classNames(styles.sidebar, className)
    const selectedItem = items[selected]
    const selectedItemId = selectedItem != null ? getProjectTreeItemId(selectedItem) : undefined
    return (
      <>
        <Shortcuts scope={this.shortcutsScope} map={this.shortcutsMap} />
        <div // It listens to keydown events from children
          role="presentation"
          className={classes}
          ref={this.sidebarRef}
          onKeyDown={this.onKeyDown}
        >
          {this.props.renderSearch({
            sidebarRef: this.sidebarRef,
            inputRef: this.setSearchRef,
            selectedItemId,
            collapsed,
            onEscape: this.focusList,
          })}
          {!collapsed && rowCount > 0 && (
            <div className={classNames(styles.content, contentClassName)}>
              <div // It listens to focus events from children
                role="presentation"
                ref={this.setTreeRef}
                className={classNames(
                  styles.projectsTree,
                  itemStyles.projectsTreeItemParent,
                  itemParentClassName,
                )}
                onBlur={this.onTreeBlur}
                onFocus={this.onTreeFocus}
              >
                <Plugin placeId={PlaceIdList.SAKURA_SIDEBAR_TOP} />
                <ProjectsTreeList
                  aria-label="Projects"
                  tabIndex={-1}
                  containerProps={containerProps}
                  className={styles.list}
                  ref={this.setListRef}
                  rowCount={rowCount}
                  rowHeight={this.calculateRowHeight}
                  estimatedRowSize={REGULAR_ITEM_HEIGHT}
                  rowRenderer={this.rowRenderer}
                  onScrollbarPresenceChange={this.onScrollbarPresenceChange}
                  scrollToIndex={shouldScrollToSelected ? selected : undefined}
                  onScroll={shouldScrollToSelected ? this.onListScroll : undefined}
                  onRowsRendered={this.onRowsRendered} // we need to pass these props to trigger updates when they change
                  shouldScrollToSelected={shouldScrollToSelected}
                  selected={selected}
                  activeItem={activeItem}
                  checkedItems={checkedItems}
                  items={items}
                  showPopupFor={this.state.showPopupFor}
                />
              </div>
            </div>
          )}
          {rowCount === 0
            ? placeholder ?? (
                <Suspense>
                  <ProjectsTreePlaceholder searchActive={searchActive} searchQuery={searchQuery} />
                </Suspense>
              )
            : null}
          {children}
        </div>
      </>
    )
  }
}

export default ProjectsTree
